import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/slices/store";
import { buildCacheKey, setTabSelectedMetrics } from "src/slices/TabData/reducer";
import { arrayMove } from "@dnd-kit/sortable";

interface UseCustomizeColumnsOptions {
  tabId: string;
  exchangeId: string | number;
  defaultMetrics?: string[];
}

export function useCustomizeColumns({ tabId, exchangeId, defaultMetrics = [] }: UseCustomizeColumnsOptions) {
  const dispatch = useDispatch();
  const cacheKey = buildCacheKey(exchangeId, tabId);

  const cacheEntry = useSelector((state: RootState) => state.tabData.entries[cacheKey]);
  const permittedMetrics = cacheEntry?.permittedMetrics ?? [];
  const reduxSelectedMetrics = cacheEntry?.selectedMetrics ?? [];

  // Local selectedMetrics — mirrors Redux but updates synchronously so drag reorders
  // don't produce a one-render flash of unchecked state (same pattern as ExchangeTable
  // which uses local useState for selectedMetrics + dispatches to Redux for persistence).
  const [localSelectedMetrics, setLocalSelectedMetrics] = useState<string[]>([]);

  // hasUserInteracted: before first interaction, preserve backend order for selected metrics
  // in the dropdown list. After interaction, order follows localSelectedMetrics (same as
  // ExchangeTable's hasUserInteracted flag).
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Full ordered list of all permitted metrics — like columnOrder in ExchangeTable.
  // Tracks ordering of both selected and unselected items independently of selection state.
  const [metricOrder, setMetricOrder] = useState<string[]>([]);

  // Keep local state in sync with Redux (external changes: initial load, order reset, etc.)
  // Only sync if user hasn't interacted yet — after interaction, local state is authoritative.
  useEffect(() => {
    if (!hasUserInteracted) {
      setLocalSelectedMetrics(reduxSelectedMetrics);
    }
  }, [reduxSelectedMetrics.join(","), hasUserInteracted]);

  // Sync metricOrder when permittedMetrics first arrives or changes.
  // Preserve existing order for known metrics, append new ones at end.
  useEffect(() => {
    if (permittedMetrics.length === 0) return;
    setMetricOrder(prev => {
      const prevSet = new Set(prev);
      const incoming = new Set(permittedMetrics);
      const preserved = prev.filter(m => incoming.has(m));
      const added = permittedMetrics.filter(m => !prevSet.has(m));
      return [...preserved, ...added];
    });
  }, [permittedMetrics.join(",")]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(400);

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => setDebouncedSearchTerm(value), 300);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest(".customize-columns-btn")
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const calculateDropdownHeight = useCallback(() => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const padding = 20;
      if (spaceBelow - padding < 500) {
        setDropdownMaxHeight(Math.max(250, spaceBelow - padding));
      } else {
        setDropdownMaxHeight(400);
      }
    }
  }, []);

  // Update both local state (instant UI) and Redux (persistence)
  const updateSelection = useCallback(
    (metrics: string[]) => {
      setLocalSelectedMetrics(metrics);
      dispatch(setTabSelectedMetrics({ cacheKey, metrics }));
    },
    [dispatch, cacheKey]
  );

  const toggleColumn = useCallback(
    (metricId: string) => {
      setHasUserInteracted(true);
      const isSelected = localSelectedMetrics.includes(metricId);
      if (isSelected) {
        updateSelection(localSelectedMetrics.filter((m) => m !== metricId));
      } else {
        // Append at end — list and table are always in sync via localSelectedMetrics order
        updateSelection([...localSelectedMetrics, metricId]);
      }
    },
    [localSelectedMetrics, updateSelection]
  );

  const selectAll = useCallback(() => {
    setHasUserInteracted(true);
    updateSelection([...metricOrder]);
  }, [metricOrder, updateSelection]);

  const clearAll = useCallback(() => {
    setHasUserInteracted(true);
    updateSelection([]);
  }, [updateSelection]);

  const resetToDefaults = useCallback(() => {
    setHasUserInteracted(true);
    updateSelection([...defaultMetrics]);
  }, [defaultMetrics, updateSelection]);

  // Drag end: operate on metricOrder (full list) to allow dropping onto unselected positions.
  // Re-derive localSelectedMetrics from new order to keep list and table in sync.
  // Both local state updates happen synchronously in the same React batch — no checked-state flash.
  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;
      const isActiveSelected = localSelectedMetrics.includes(active.id as string);
      if (!over || active.id === over.id || !isActiveSelected) return;

      setHasUserInteracted(true);

      setMetricOrder(prevOrder => {
        const oldIndex = prevOrder.indexOf(active.id as string);
        const newIndex = prevOrder.indexOf(over.id as string);
        if (oldIndex === -1 || newIndex === -1) return prevOrder;

        const newOrder = arrayMove(prevOrder, oldIndex, newIndex);
        const selectedSet = new Set(localSelectedMetrics);
        // Re-derive selected in new metricOrder sequence — keeps list and table in sync
        const reorderedSelected = newOrder.filter((m: string) => selectedSet.has(m));

        setLocalSelectedMetrics(reorderedSelected);
        dispatch(setTabSelectedMetrics({ cacheKey, metrics: reorderedSelected }));

        return newOrder;
      });
    },
    [localSelectedMetrics, cacheKey, dispatch]
  );

  const areAllSelected = useMemo(
    () => permittedMetrics.length > 0 && localSelectedMetrics.length === permittedMetrics.length,
    [localSelectedMetrics, permittedMetrics]
  );

  // Dropdown display order:
  // Before user interaction: selected metrics in permittedMetrics (backend) order, then unselected
  // After user interaction:  selected metrics in localSelectedMetrics order (reflects DnD + toggles),
  //                          then unselected in metricOrder order
  // This matches ExchangeTable's hasUserInteracted pattern and keeps list/table always in sync.
  const sortedMetricsList = useMemo(() => {
    const selectedSet = new Set(localSelectedMetrics);
    const unselected = metricOrder.filter(m => !selectedSet.has(m));

    if (!hasUserInteracted) {
      // Preserve backend order for selected metrics on initial load
      const selectedInBackendOrder = metricOrder.filter(m => selectedSet.has(m));
      return [...selectedInBackendOrder, ...unselected];
    }

    // After interaction: selected in localSelectedMetrics order (authoritative — matches table)
    return [...localSelectedMetrics, ...unselected];
  }, [metricOrder, localSelectedMetrics, hasUserInteracted]);

  const filteredMetricsList = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return sortedMetricsList;
    const term = debouncedSearchTerm.toLowerCase();
    return sortedMetricsList.filter((m) => m.toLowerCase().includes(term));
  }, [sortedMetricsList, debouncedSearchTerm]);

  return {
    selectedMetrics: localSelectedMetrics,
    permittedMetrics,
    metricOrder,
    dropdownOpen,
    searchTerm,
    dropdownMaxHeight,
    dropdownRef,
    buttonRef,
    toggleColumn,
    selectAll,
    clearAll,
    resetToDefaults,
    setDropdownOpen,
    handleSearchChange,
    handleDragEnd,
    calculateDropdownHeight,
    areAllSelected,
    filteredMetricsList,
  };
}
